import { Draft } from 'immer'
import { Get } from 'type-fest'
import { v4 as uuidV4 } from 'uuid'
import {
  type NotificationMode,
  pendingNotificationTargetsAfterUpdate,
} from '@opallabs/concepts'
import { FieldValues } from '@opallabs/satellite'
import { PlanResponse } from '../types'

// `custom_field_values` is keyed by block id, then by custom field id, for an
// O(1) lookup of a block's value for a given field.
type BlockCustomFieldValue = NonNullable<
  Get<PlanResponse, ['data', 'custom_field_values', string, string]>
>

export type BlockCustomFieldValueType = BlockCustomFieldValue['type']

type CreateParams = {
  blockId: string
  fieldId: string
  value: FieldValues
  /**
   * Custom field system type (e.g. "date", "textarea"). Required so the
   * placeholder is interpreted by cell-value selectors that branch on type.
   */
  type: BlockCustomFieldValue['type']
  /**
   * Returned by `tempId()` and stamped onto the placeholder so the mutation's
   * onSuccess can swap it for the server-assigned id.
   */
  tempId: string
  notificationMode?: NotificationMode
}

type ReplaceTempIdParams = {
  blockId: string
  tempId: string
  realId: string
}

type UpdateParams = {
  blockId: string
  fieldValueId: string
  /** Omitted by a mode-only edit, which leaves the value alone. */
  value?: FieldValues | null
  notificationMode?: NotificationMode
  /**
   * Overwrites the pending set outright, which is how Notify empties it the
   * moment the button is pressed rather than a round trip later.
   */
  pendingNotificationTargets?: { user_ids: string[]; group_ids: string[] }
}

type DeleteParams = {
  blockId: string
  fieldValueId: string
}

/**
 * Optimistically inserts a placeholder CFV into the plan response's
 * `data.custom_field_values[blockId]` map under the custom field's id, with a
 * `temp-${uuid}` record id. A direct value keyed by field id supersedes any
 * inherited value for the same field, matching the server's precedence rules.
 * The mutation's onSuccess swaps the temp id for the real one via `replaceTempId`.
 */
export const createBlockCustomFieldValue = ({
  blockId,
  fieldId,
  value,
  type,
  tempId,
  notificationMode,
}: CreateParams) => {
  return (draftResponse: Draft<PlanResponse>) => {
    if (!draftResponse.data.custom_field_values) {
      draftResponse.data.custom_field_values = {}
    }
    const blockValues = (draftResponse.data.custom_field_values[blockId] ??= {})
    blockValues[fieldId] = {
      id: tempId,
      custom_field_id: fieldId,
      type,
      // `FieldValues` is wider than the response schema; cast only this property
      value: value as BlockCustomFieldValue['value'],
      association: 'direct',
      pending_notification_targets: { user_ids: [], group_ids: [] },
      // Without a mode sent, the server seeds it from the field's default,
      // and until the response lands the picker has none. `Partial` is what
      // lets the placeholder leave it out; the response schema requires it.
      notification_mode: notificationMode,
    } as Partial<BlockCustomFieldValue> as BlockCustomFieldValue

    return draftResponse
  }
}

/**
 * Swaps a temporary id for the server-assigned id once a create resolves.
 */
export const replaceTempId = ({
  blockId,
  tempId,
  realId,
}: ReplaceTempIdParams) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const blockValues = draftResponse.data.custom_field_values?.[blockId]
    if (!blockValues) return draftResponse

    const target = Object.values(blockValues).find(
      (entry) => entry?.id === tempId
    )
    if (target) target.id = realId
    return draftResponse
  }
}

export const updateBlockCustomFieldValue = ({
  blockId,
  fieldValueId,
  value,
  notificationMode,
  pendingNotificationTargets,
}: UpdateParams) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const blockValues = draftResponse.data.custom_field_values?.[blockId]
    if (!blockValues) return draftResponse

    const target = Object.values(blockValues).find(
      (entry) => entry?.id === fieldValueId
    )
    if (!target) return draftResponse

    const previous = {
      value: target.value,
      pendingTargets: target.pending_notification_targets,
    }

    // Only what the caller sent moves: a mode-only edit must not clear the
    // picker, and a value-only edit must not invent a mode.
    if (value !== undefined) target.value = value as typeof target.value
    if (notificationMode !== undefined) {
      target.notification_mode = notificationMode
    }

    if (value && 'user_ids' in value) {
      target.pending_notification_targets =
        pendingNotificationTargetsAfterUpdate({
          previous,
          next: value,
          notificationMode: target.notification_mode,
        })
    }

    if (pendingNotificationTargets !== undefined) {
      target.pending_notification_targets = pendingNotificationTargets
    }

    return draftResponse
  }
}

export const deleteBlockCustomFieldValue = ({
  blockId,
  fieldValueId,
}: DeleteParams) => {
  return (draftResponse: Draft<PlanResponse>) => {
    const blockValues = draftResponse.data.custom_field_values?.[blockId]
    if (!blockValues) return draftResponse

    const fieldId = Object.keys(blockValues).find(
      (key) => blockValues[key]?.id === fieldValueId
    )
    if (fieldId) delete blockValues[fieldId]
    return draftResponse
  }
}

/** Returns a fresh temp id for the optimistic create. */
export const tempCFVId = () => `temp-${uuidV4()}`
